import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type HTMLAttributes,
} from "react";
import {
  Button,
  ButtonBase,
  Card,
  EmptyState,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NativeSelect,
  Skeleton,
  StatusBadge,
} from "@crewfsm/ui/app";
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  CircleAlert,
  LayoutGrid,
  GripVertical,
  List,
  RefreshCw,
  Search,
} from "lucide-react";
import type {
  WorkflowPipelineColumn,
  WorkflowPipelineItem,
  WorkflowPipelineResource,
  WorkflowPipelineResponse,
  WorkflowPipelineStage,
  WorkflowPipelineView,
} from "@crewfsm/contracts";
import type { PipelineAppSession } from "./app-session";
import { createPipelineApi, PipelineApiError } from "./pipeline-api";
import {
  ageLabel,
  displayDate,
  money,
  ownerLabel,
  PAYMENT_LABELS,
  WORKFLOW_LABELS,
  WORKFLOW_VIEWS,
} from "./workflow-model";

import {
  WorkflowActionMenu,
  WorkflowActionPanel,
  type OpenWorkflowAction,
  type PipelineAction,
} from "./workflow-actions";

import { isLeadAction, LeadActionPanel } from "./lead-actions";
import {
  useWorkflowMovement,
  workflowMoves,
  WorkflowMoveMenu,
} from "./workflow-movement";

const READ_PERMISSIONS = [
  "leads:read",
  "estimates:read",
  "jobs:read",
  "appointments:read",
  "invoices:read",
  "payments:read",
];
type Filters = {
  search: string;
  view: WorkflowPipelineView;
  followUp?: "OVERDUE" | "NONE";
};
const INITIAL_FILTERS: Filters = { search: "", view: "ALL" };

export function WorkflowBoard({
  session,
  initialView = "ALL",
}: {
  session: PipelineAppSession;
  initialView?: WorkflowPipelineView;
}) {
  const api = useMemo(
    () =>
      createPipelineApi({
        getSessionToken: () => session.bridge.getSessionToken(),
        refreshSessionToken: async () => {
          await session.bridge.refreshContext();
          return session.bridge.getSessionToken();
        },
      }),
    [session.bridge],
  );
  const [filters, setFilters] = useState<Filters>({
    ...INITIAL_FILTERS,
    view: initialView,
  });
  const canRead = (
    filters.view === "LEADS" ? ["leads:read"] : READ_PERMISSIONS
  ).every((permission) =>
    session.context.effectivePermissions.includes(permission),
  );
  const [draft, setDraft] = useState("");
  const [data, setData] = useState<WorkflowPipelineResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [denied, setDenied] = useState(false);
  const [loadingStage, setLoadingStage] =
    useState<WorkflowPipelineStage | null>(null);
  const [layout, setLayout] = useState<"BOARD" | "TABLE">("BOARD");
  const [mobileStage, setMobileStage] = useState<WorkflowPipelineStage | "">(
    "",
  );
  const [selectedAction, setSelectedAction] = useState<{
    item: WorkflowPipelineItem;
    action: PipelineAction;
    trigger: HTMLButtonElement | null;
  } | null>(null);
  const [notice, setNotice] = useState("");
  useEffect(() => {
    setSelectedAction(null);
    setNotice("");
  }, [session.context.locationId]);
  const generation = useRef(0);
  const boardRef = useRef<HTMLDivElement>(null);
  const query = useMemo(
    () => ({ ...filters, locationId: session.context.locationId, limit: 20 }),
    [filters, session.context.locationId],
  );
  const load = useCallback(async () => {
    const current = ++generation.current;
    setLoading(true);
    setError(null);
    setDenied(false);
    setLoadingStage(null);
    try {
      const result = await api.board(query);
      if (current === generation.current) setData(result);
    } catch (cause) {
      if (current === generation.current) {
        setError(
          cause instanceof Error
            ? cause.message
            : "The pipeline could not load.",
        );
        setDenied(cause instanceof PipelineApiError && cause.status === 403);
      }
    } finally {
      if (current === generation.current) setLoading(false);
    }
  }, [api, query]);
  const movement = useWorkflowMovement({
    scope: JSON.stringify([
      session.context.locationId,
      session.context.effectivePermissions,
      filters,
      layout,
    ]),
    api,
    permissions: session.context.effectivePermissions,
    blocked:
      loading || Boolean(loadingStage) || Boolean(selectedAction) || !canRead,
    onAction: (item, action, trigger) => {
      setNotice("");
      setSelectedAction({ item, action, trigger });
    },
    onSaved: async (message, stage) => {
      setNotice(message);
      setMobileStage(stage);
      await load();
      boardRef.current?.focus({ preventScroll: true });
    },
    onError: setError,
  });
  const interaction = useRef(movement.interacting);
  interaction.current = movement.interacting;
  useEffect(() => {
    void session.bridge.setTitle("Pipeline");
    if (canRead) void load();
    return () => {
      generation.current++;
    };
  }, [canRead, load, session.bridge]);
  useEffect(() => {
    if (!canRead) return;
    let refreshTimer: ReturnType<typeof setTimeout> | undefined;
    const refresh = () => {
      clearTimeout(refreshTimer);
      // Focus precedes pointerdown when entering the iframe. Let an intended
      // drag start before deciding whether the board can refresh safely.
      refreshTimer = setTimeout(() => {
        if (document.visibilityState === "visible" && !interaction.current())
          void load();
      }, 200);
    };
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);
    return () => {
      clearTimeout(refreshTimer);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
    };
  }, [canRead, load]);
  const loadMore = async (column: WorkflowPipelineColumn) => {
    if (!column.pageInfo.nextCursor || loadingStage || loading) return;
    const current = generation.current;
    setLoadingStage(column.stage);
    setError(null);
    try {
      const result = await api.board({
        ...query,
        stage: column.stage,
        cursor: column.pageInfo.nextCursor,
      });
      if (current !== generation.current) return;
      const next = result.columns.find((item) => item.stage === column.stage);
      if (next)
        setData((previous) =>
          previous
            ? {
                ...previous,
                columns: previous.columns.map((existing) =>
                  existing.stage === column.stage
                    ? {
                        ...next,
                        items: [
                          ...existing.items,
                          ...next.items.filter(
                            (item) =>
                              !existing.items.some((old) => old.id === item.id),
                          ),
                        ],
                      }
                    : existing,
                ),
              }
            : previous,
        );
    } catch (cause) {
      if (current === generation.current)
        setError(
          cause instanceof Error
            ? cause.message
            : "More work could not load. Try again.",
        );
    } finally {
      if (current === generation.current) setLoadingStage(null);
    }
  };
  const onAction: OpenWorkflowAction = (item, action, trigger) => {
    movement.dismissContinuation();
    setNotice("");
    setSelectedAction({ item, action, trigger });
  };
  const open = (resource: WorkflowPipelineResource) => {
    void session.bridge
      .openResource(resource.type, resource.id)
      .catch(() => setError("The detail page could not open. Try again."));
  };
  const change = (next: Filters) => {
    if (movement.movingId) return;
    setData(null);
    setFilters(next);
    setMobileStage("");
  };
  const clear = () => {
    setDraft("");
    change(INITIAL_FILTERS);
  };
  const filtered = Boolean(
    filters.search || filters.followUp || filters.view !== "ALL",
  );
  const selectedStage = data?.columns.some(
    (column) => column.stage === mobileStage,
  )
    ? mobileStage
    : (data?.columns.find((column) => column.total > 0)?.stage ??
      data?.columns[0]?.stage);
  const scroll = (direction: number) =>
    boardRef.current?.scrollBy({
      left: direction * 310,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    });

  if (!canRead || denied)
    return (
      <main className="workflow-theme workflow-shell">
        <EmptyState
          icon={<CircleAlert />}
          title="Update Pipeline access"
          description="An owner or admin can approve access to the full workflow in app settings."
          action={
            session.context.effectivePermissions.includes("leads:read") &&
            filters.view !== "LEADS" ? (
              <Button
                variant="outline"
                onClick={() => change({ ...INITIAL_FILTERS, view: "LEADS" })}
              >
                Show leads
              </Button>
            ) : undefined
          }
        />
      </main>
    );
  return (
    <main className="workflow-theme workflow-shell" aria-busy={loading}>
      <header className="workflow-header">
        <div className="workflow-heading">
          <h1 className="shrink-0 text-lg font-semibold tracking-tight">
            Pipeline
          </h1>
          <p className="min-w-0 truncate text-sm text-muted-foreground">
            {data ? (
              <>
                {data.multiLocationEnabled !== false ? (
                  <span className="sr-only md:not-sr-only">
                    {data.location.name} ·{" "}
                  </span>
                ) : null}
                {data.total} work item{data.total === 1 ? "" : "s"}
              </>
            ) : (
              "From first request to final payment"
            )}
          </p>
        </div>
        <form
          className="workflow-toolbar"
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            change({ ...filters, search: draft.trim() });
          }}
        >
          <InputGroup className="h-10 min-w-0 grow basis-full bg-card md:h-9 md:basis-0">
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              aria-label="Search pipeline"
              disabled={Boolean(movement.movingId)}
              placeholder="Search customer, work, or document number"
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
            />
          </InputGroup>
          <NativeSelect
            aria-label="Workflow section"
            disabled={Boolean(movement.movingId)}
            className="workflow-select min-w-0 flex-1 [&_select]:h-10 md:flex-none md:[&_select]:h-9"
            value={filters.view}
            onChange={(event) =>
              change({
                ...filters,
                view: event.target.value as WorkflowPipelineView,
              })
            }
          >
            {Object.entries(WORKFLOW_VIEWS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label="Follow-up filter"
            disabled={Boolean(movement.movingId)}
            className="workflow-select min-w-0 flex-1 [&_select]:h-10 md:flex-none md:[&_select]:h-9"
            value={filters.followUp ?? ""}
            onChange={(event) =>
              change({
                ...filters,
                followUp:
                  (event.target.value as Filters["followUp"]) || undefined,
              })
            }
          >
            <option value="">Any follow-up</option>
            <option value="OVERDUE">Overdue follow-up</option>
            <option value="NONE">No follow-up</option>
          </NativeSelect>
          <Button
            type="submit"
            variant="outline"
            className="h-10 md:h-9"
            disabled={Boolean(movement.movingId)}
          >
            Apply
          </Button>
          {filtered ? (
            <Button
              variant="ghost"
              className="h-10 md:h-9"
              onClick={clear}
              disabled={Boolean(movement.movingId)}
            >
              Clear
            </Button>
          ) : null}
        </form>
        <div className="workflow-header-actions flex shrink-0 items-center gap-2">
          <div
            className="workflow-layout"
            role="group"
            aria-label="View layout"
          >
            <Button
              size="icon"
              className="size-10 md:size-8"
              variant={layout === "BOARD" ? "secondary" : "ghost"}
              aria-label="Board view"
              disabled={Boolean(movement.movingId)}
              aria-pressed={layout === "BOARD"}
              onClick={() => setLayout("BOARD")}
            >
              <LayoutGrid />
            </Button>
            <Button
              size="icon"
              className="size-10 md:size-8"
              variant={layout === "TABLE" ? "secondary" : "ghost"}
              aria-label="Table view"
              disabled={Boolean(movement.movingId)}
              aria-pressed={layout === "TABLE"}
              onClick={() => setLayout("TABLE")}
            >
              <List />
            </Button>
          </div>
          <Button
            variant="outline"
            className="h-10 md:h-9"
            disabled={loading || Boolean(movement.movingId)}
            onClick={() => void load()}
            aria-label="Refresh pipeline"
          >
            <RefreshCw
              className={
                loading ? "animate-spin motion-reduce:animate-none" : ""
              }
            />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
          {layout === "BOARD" ? (
            <div className="hidden items-center gap-1 border-l border-border pl-2 md:flex">
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Previous stages"
                disabled={loading || !data?.total}
                onClick={() => scroll(-1)}
              >
                <ArrowLeft />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Next stages"
                disabled={loading || !data?.total}
                onClick={() => scroll(1)}
              >
                <ArrowRight />
              </Button>
            </div>
          ) : null}
        </div>
      </header>
      {movement.continuation ? (
        <div className="workflow-continuation" role="status">
          <div className="min-w-0 flex-1 basis-full text-sm [overflow-wrap:anywhere] md:basis-0">
            <p className="font-medium">{movement.continuation.title}</p>
            <p>
              {movement.continuation.instruction} The card updates when that
              step is complete.
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => open(movement.continuation!.resource)}
          >
            Open {movement.continuation.resource.type.toLowerCase()}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={movement.dismissContinuation}
          >
            Cancel
          </Button>
        </div>
      ) : null}
      <p id="pipeline-drag-help" className="sr-only">
        Drag a card by its grip to a highlighted stage. You can also use its
        Move menu with a keyboard or touch.
      </p>
      {error ? (
        <div className="workflow-error" role="alert">
          <CircleAlert className="size-4 shrink-0" />
          <span>{error}</span>
          <Button variant="outline" size="sm" onClick={() => void load()}>
            Retry
          </Button>
        </div>
      ) : null}
      {loading && !data ? (
        <div
          className="workflow-loading"
          role="status"
          aria-label="Loading pipeline"
        >
          {[1, 2, 3, 4].map((index) => (
            <div key={index}>
              <Skeleton className="mb-4 h-5 w-32" />
              <Skeleton className="mb-3 h-36 w-full" />
              <Skeleton className="h-36 w-full" />
            </div>
          ))}
        </div>
      ) : null}
      {!loading && data?.total === 0 ? (
        <EmptyState
          className="flex-1 border-0"
          icon={<LayoutGrid />}
          title={filtered ? "No matching work" : "Your pipeline is clear"}
          description={
            filtered
              ? "Adjust the filters to see more work."
              : "Leads, estimates, and jobs appear here as work progresses."
          }
          action={
            filtered ? (
              <Button variant="outline" onClick={clear}>
                Clear filters
              </Button>
            ) : undefined
          }
        />
      ) : data ? (
        <>
          {layout === "BOARD" ? (
            <>
              <NativeSelect
                className="workflow-mobile-stage w-full [&_select]:h-10"
                aria-label="Visible stage"
                disabled={Boolean(movement.movingId)}
                value={selectedStage ?? ""}
                onChange={(event) =>
                  setMobileStage(event.target.value as WorkflowPipelineStage)
                }
              >
                {data.columns.map((column) => (
                  <option key={column.stage} value={column.stage}>
                    {WORKFLOW_LABELS[column.stage]} ({column.total})
                  </option>
                ))}
              </NativeSelect>
              <div
                className="workflow-board"
                data-drag-active={Boolean(movement.dragged)}
                {...movement.boardProps}
                ref={boardRef}
                tabIndex={0}
                role="region"
                aria-label="Workflow stages, scroll horizontally for more"
              >
                {data.columns.map((column) => (
                  <section
                    key={column.stage}
                    className="workflow-column"
                    {...movement.columnProps(column.stage)}
                    data-mobile-active={column.stage === selectedStage}
                    aria-label={`${WORKFLOW_LABELS[column.stage]}, ${column.total} work items`}
                  >
                    <header className="workflow-column-header">
                      <h2>{WORKFLOW_LABELS[column.stage]}</h2>
                      <span className="tabular-nums text-muted-foreground">
                        {column.total}
                      </span>
                    </header>
                    {movement.dragged &&
                    workflowMoves(
                      movement.dragged,
                      session.context.effectivePermissions,
                    ).find((move) => move.stage === column.stage) ? (
                      <p className="workflow-drop-hint">
                        {
                          workflowMoves(
                            movement.dragged,
                            session.context.effectivePermissions,
                          ).find((move) => move.stage === column.stage)!.hint
                        }
                      </p>
                    ) : null}
                    <div className="workflow-cards">
                      {column.items.map((item) => (
                        <WorkflowCard
                          key={item.id}
                          item={item}
                          onOpen={open}
                          onAction={movement.movingId ? undefined : onAction}
                          permissions={session.context.effectivePermissions}
                          dragProps={movement.cardProps(item)}
                          moveControl={
                            workflowMoves(
                              item,
                              session.context.effectivePermissions,
                            ).length ? (
                              <WorkflowMoveMenu
                                item={item}
                                moves={workflowMoves(
                                  item,
                                  session.context.effectivePermissions,
                                )}
                                disabled={
                                  loading ||
                                  Boolean(loadingStage) ||
                                  Boolean(movement.movingId)
                                }
                                onMove={movement.move}
                              />
                            ) : undefined
                          }
                        />
                      ))}
                      {!column.total ? (
                        <p className="workflow-column-empty">
                          No work in this stage
                        </p>
                      ) : null}
                      {column.pageInfo.hasNextPage ? (
                        <Button
                          variant="outline"
                          className="w-full"
                          disabled={
                            Boolean(loadingStage) ||
                            loading ||
                            Boolean(movement.movingId)
                          }
                          onClick={() => void loadMore(column)}
                        >
                          {loadingStage === column.stage
                            ? "Loading…"
                            : `Load more (${column.items.length} of ${column.total})`}
                        </Button>
                      ) : null}
                    </div>
                  </section>
                ))}
              </div>
            </>
          ) : (
            <WorkflowTable
              columns={data.columns}
              onOpen={open}
              onAction={movement.movingId ? undefined : onAction}
              permissions={session.context.effectivePermissions}
              onLoadMore={(column) => void loadMore(column)}
              loadingStage={loadingStage}
            />
          )}
          <p className="workflow-footnote" role="status">
            {movement.movingId
              ? "Moving lead… "
              : movement.dragged
                ? `Moving ${movement.dragged.title}. Drop on a highlighted stage. `
                : notice
                  ? `${notice} `
                  : ""}
            Updated{" "}
            {new Intl.DateTimeFormat("en-US", {
              hour: "numeric",
              minute: "2-digit",
              timeZone: data.location.timezone,
            }).format(new Date(data.refreshedAt))}{" "}
            · {data.location.timezone}
          </p>
        </>
      ) : null}
      {notice && data?.total === 0 ? (
        <p className="workflow-footnote" role="status">
          {notice}
        </p>
      ) : null}
      {selectedAction && data
        ? (() => {
            const props = {
              item: selectedAction.item,
              api,
              onClose: () => setSelectedAction(null),
              onSaved: (message: string) => {
                setNotice(message);
                setSelectedAction(null);
                void load();
              },
              onReturnFocus: () => {
                if (selectedAction.trigger?.isConnected)
                  selectedAction.trigger.focus();
                else boardRef.current?.focus();
              },
            };
            const key = `${selectedAction.item.id}:${selectedAction.action}:${data.location.id}`;
            return isLeadAction(selectedAction.action) ? (
              <LeadActionPanel
                key={key}
                {...props}
                action={selectedAction.action}
              />
            ) : (
              <WorkflowActionPanel
                key={key}
                {...props}
                action={selectedAction.action}
                locationId={data.location.id}
              />
            );
          })()
        : null}
    </main>
  );
}

export function WorkflowCard({
  item,
  onOpen,
  onAction,
  permissions,
  dragProps,
  moveControl,
}: {
  item: WorkflowPipelineItem;
  dragProps?: HTMLAttributes<HTMLDivElement>;
  moveControl?: React.ReactNode;
  onAction?: OpenWorkflowAction;
  permissions?: readonly string[];
  onOpen: (resource: WorkflowPipelineResource) => void;
}) {
  const priority = item.priority === "HIGH" || item.priority === "URGENT";
  const closed = item.stage === "CLOSED";
  const hasWorkTitle = item.title.trim() !== item.resource.label.trim();
  const title = hasWorkTitle ? item.title : item.customerName;
  const relatedResources = item.resources.filter(
    (resource) =>
      resource.type !== item.resource.type || resource.id !== item.resource.id,
  );
  return (
    <Card
      className="workflow-card"
      {...dragProps}
      aria-describedby={dragProps?.draggable ? "pipeline-drag-help" : undefined}
    >
      <div className="workflow-card-heading">
        <span
          className="workflow-card-label text-xs text-muted-foreground"
          title={dragProps?.draggable ? "Drag to another stage" : undefined}
        >
          {dragProps?.draggable ? (
            <GripVertical
              aria-hidden="true"
              className="pointer-events-none size-3.5 shrink-0"
            />
          ) : null}
          {item.resource.label}
        </span>
        {priority ? (
          <StatusBadge tone={item.priority === "URGENT" ? "danger" : "warning"}>
            {item.priority === "URGENT" ? "Urgent" : "High"}
          </StatusBadge>
        ) : null}
        {moveControl}
        <span data-no-drag>
          <WorkflowActionMenu
            item={item}
            onAction={onAction}
            permissions={permissions}
          />
        </span>
      </div>
      <ButtonBase
        className="workflow-card-main"
        data-no-drag
        role="link"
        aria-label={`Open ${item.resource.label}: ${title}`}
        onClick={() => onOpen(item.resource)}
      >
        <span className="block text-sm font-semibold leading-snug [overflow-wrap:anywhere]">
          {title}
        </span>
        {hasWorkTitle ? (
          <span className="mt-1 block text-xs text-muted-foreground [overflow-wrap:anywhere]">
            {item.customerName}
          </span>
        ) : null}
        <span className="mt-3 flex flex-wrap items-center justify-between gap-2 text-xs">
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {ownerLabel(item)}
          </span>
          <span className="tabular-nums font-medium">
            {item.amountCents === null ? "" : money(item.amountCents)}
          </span>
        </span>
        {item.appointmentAt &&
        (item.stage === "SCHEDULED" || item.stage === "IN_PROGRESS") ? (
          <span className="mt-2 flex items-start gap-1.5 text-xs text-muted-foreground">
            <CalendarClock className="pointer-events-none size-3.5 shrink-0" />
            {displayDate(item.appointmentAt, item.timezone)}
          </span>
        ) : null}
        {item.followUpAt ? (
          <span
            className={`mt-2 block text-xs ${item.followUpOverdue ? "text-destructive" : "text-muted-foreground"}`}
          >
            {item.followUpOverdue ? "Overdue follow-up" : "Follow up"} ·{" "}
            {displayDate(item.followUpAt, item.timezone)}
          </span>
        ) : null}
        {item.holdReason ? (
          <span className="mt-2 block text-xs text-muted-foreground">
            {sentence(item.holdReason)} ·{" "}
            {WORKFLOW_LABELS[item.underlyingStage]}
          </span>
        ) : null}
        {closed ? (
          <span className="mt-2 block text-xs text-muted-foreground">
            {sentence(item.status)}
          </span>
        ) : null}
        {item.paymentStatus !== "NOT_INVOICED" ? (
          <span className="mt-2 flex flex-wrap items-center justify-between gap-2">
            <StatusBadge
              tone={
                item.paymentStatus === "PAID"
                  ? "success"
                  : item.paymentStatus === "PAST_DUE"
                    ? "danger"
                    : "neutral"
              }
            >
              {PAYMENT_LABELS[item.paymentStatus]}
            </StatusBadge>
            {item.balanceCents !== null && item.balanceCents > 0 ? (
              <span className="text-xs tabular-nums">
                {money(item.balanceCents)} due
              </span>
            ) : null}
          </span>
        ) : null}
      </ButtonBase>
      <div
        className={
          relatedResources.length
            ? "workflow-card-footer"
            : "workflow-card-updated"
        }
        data-no-drag
      >
        {relatedResources.length ? (
          <div
            className="flex flex-wrap gap-x-3 gap-y-1"
            aria-label="Related records"
          >
            {relatedResources.map((resource) => (
              <Button
                key={`${resource.type}:${resource.id}`}
                variant="link"
                className="h-8 text-xs md:h-7"
                role="link"
                aria-label={`Open ${resource.label}`}
                onClick={() => onOpen(resource)}
              >
                {resource.label}
              </Button>
            ))}
          </div>
        ) : null}
        <span className="block text-[11px] text-muted-foreground">
          {ageLabel(item.updatedAt)}
        </span>
      </div>
    </Card>
  );
}

function WorkflowTable({
  columns,
  onOpen,
  onAction,
  onLoadMore,
  loadingStage,
  permissions,
}: {
  columns: WorkflowPipelineColumn[];
  onAction?: OpenWorkflowAction;
  permissions: readonly string[];
  onOpen: (resource: WorkflowPipelineResource) => void;
  onLoadMore: (column: WorkflowPipelineColumn) => void;
  loadingStage: WorkflowPipelineStage | null;
}) {
  return (
    <div
      className="workflow-table-frame"
      tabIndex={0}
      role="region"
      aria-label="Pipeline table"
    >
      <table className="workflow-table">
        <caption className="sr-only">
          Work from first request to final payment
        </caption>
        <thead>
          <tr>
            <th>Work</th>
            <th>Stage</th>
            <th>Owner</th>
            <th>Payment</th>
            <th className="text-right">Value</th>
            <th>Related records</th>
            <th>
              <span className="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {columns.flatMap((column) => [
            ...column.items.map((item) => (
              <tr key={item.id}>
                <td>
                  <Button
                    variant="link"
                    role="link"
                    className="h-auto text-left whitespace-normal"
                    onClick={() => onOpen(item.resource)}
                  >
                    {item.title}
                  </Button>
                  <span className="mt-1 block text-xs text-muted-foreground">
                    {item.customerName}
                  </span>
                </td>
                <td>{WORKFLOW_LABELS[item.stage]}</td>
                <td>{ownerLabel(item)}</td>
                <td>{PAYMENT_LABELS[item.paymentStatus]}</td>
                <td className="text-right tabular-nums">
                  {item.amountCents === null ? "—" : money(item.amountCents)}
                </td>
                <td>
                  <div className="flex flex-wrap gap-2">
                    {item.resources.map((resource) => (
                      <Button
                        key={resource.id}
                        variant="link"
                        role="link"
                        className="h-8 text-xs"
                        onClick={() => onOpen(resource)}
                      >
                        {resource.label}
                      </Button>
                    ))}
                  </div>
                </td>
                <td>
                  <WorkflowActionMenu
                    item={item}
                    onAction={onAction}
                    permissions={permissions}
                  />
                </td>
              </tr>
            )),
            ...(column.pageInfo.hasNextPage
              ? [
                  <tr key={`more-${column.stage}`}>
                    <td colSpan={7}>
                      <Button
                        variant="outline"
                        disabled={Boolean(loadingStage)}
                        onClick={() => onLoadMore(column)}
                      >
                        Load more {WORKFLOW_LABELS[column.stage].toLowerCase()}{" "}
                        ({column.items.length} of {column.total})
                      </Button>
                    </td>
                  </tr>,
                ]
              : []),
          ])}
        </tbody>
      </table>
    </div>
  );
}
function sentence(value: string): string {
  return value
    .toLowerCase()
    .replaceAll("_", " ")
    .replace(/^./, (letter) => letter.toUpperCase());
}
